import {logAnalyticsEvent} from '@/services/analyticsLogger';

type AiFlow = 'trip_planning' | 'activity_suggestions' | 'trip_companion';
type Tier = 'free' | 'premium' | 'unknown';

type RequestMeta = {
  flow: AiFlow;
  endpoint: string;
  tier?: Tier;
  intent?: string;
};

const safeTrack = async (fn: () => Promise<void>): Promise<void> => {
  try {
    await fn();
  } catch (error) {
    console.warn('[aiFlowAnalytics] failed to log event', error);
  }
};

export const trackAiRequestStarted = async (meta: RequestMeta) =>
  safeTrack(() =>
    logAnalyticsEvent('ai_request_started', {
      flow: meta.flow,
      endpoint: meta.endpoint,
      intent: meta.intent,
      tier: meta.tier ?? 'unknown',
    }),
  );

export const trackAiRequestCompleted = async (
  meta: RequestMeta & {latencyMs: number; resultCount?: number},
) =>
  safeTrack(() =>
    logAnalyticsEvent('ai_request_completed', {
      flow: meta.flow,
      endpoint: meta.endpoint,
      latencyMs: meta.latencyMs,
      resultCount: meta.resultCount,
      tier: meta.tier ?? 'unknown',
    }),
  );

export const trackAiRequestFailed = async (
  meta: RequestMeta & {code?: string; message?: string},
) =>
  safeTrack(() =>
    logAnalyticsEvent('ai_request_failed', {
      flow: meta.flow,
      endpoint: meta.endpoint,
      code: meta.code,
      message: meta.message,
      tier: meta.tier ?? 'unknown',
    }),
  );

export const trackAiFallbackUsed = async (
  meta: RequestMeta & {reason?: string},
) =>
  safeTrack(() =>
    logAnalyticsEvent('ai_fallback_used', {
      flow: meta.flow,
      endpoint: meta.endpoint,
      reason: meta.reason,
    }),
  );

export const trackAiResultAccepted = async (
  meta: RequestMeta & {resultType?: string; resultId?: string},
) =>
  safeTrack(() =>
    logAnalyticsEvent('ai_result_accepted', {
      flow: meta.flow,
      endpoint: meta.endpoint,
      resultType: meta.resultType,
      resultId: meta.resultId,
    }),
  );

export const trackAiFeedbackSubmitted = async (
  meta: RequestMeta & {value: 'helpful' | 'not_helpful'; messageId?: string},
) =>
  safeTrack(() =>
    logAnalyticsEvent('ai_feedback_submitted', {
      flow: meta.flow,
      endpoint: meta.endpoint,
      value: meta.value,
      messageId: meta.messageId,
    }),
  );
